import Application from "../../Application";
import { ReportConstants } from "../../common/Constants";
import ShowErrorMessageInteractor from "../../interactor/notifications/ShowErrorMessageInteractor";
import ShowSuccessMessageInteractor from "../../interactor/notifications/ShowSuccessMessageInteractor";
import GetApplicantsInteractor from "../../interactor/report/GetApplicantsInteractor";
import GetFormStructureInteractor from "../../interactor/report/GetFormStructureInteractor";
import GetFormTypeListInteractor from "../../interactor/report/GetFormTypeListInteractor";
import GetReport7DataInteractor from "../../interactor/report/GetReport7DataInteractor";
import { IFormStructureSection } from "../../model/report/applicantdata/FormStructureSection";
import { IFormTypeItem } from "../../model/report/FormTypeItem";
import { IReport6Applicant } from "../../model/report/Report6Applicant";
import { IReport7Applicant } from "../../model/report/report7/Report7Applicant";
import { IReportingPeriodItem } from "../../model/report/ReportingPeriodItem";
import {
  FormStructureFlatListMapper,
  FormStructureMapper
} from "../../service/report/mappers/FormStructureMapper";
import Report7ApplicantsMapper from "../../service/report/mappers/Report7ApplicantsMapper";
import withStore, { TLoadingAwarePresenter, TPresentable } from "../withStore";
import Report7ApplicantPresentationModel, {
  IReport7ApplicantPresentationModel
} from "./model/Report7ApplicantPresentationModel";
import Report7NumericValuePresentationModelList from "./model/Report7NumericValuePresentationModel";

export interface TReport7Presenter extends TLoadingAwarePresenter {
  reportingPeriods: IReportingPeriodItem[];
  formTypes: IFormTypeItem[];
  applicants: IReport6Applicant[];
  isFormTypesSelectDisabled: boolean;
  formStructure: IFormStructureSection[];
  flatFormStructure: IFormStructureSection[];
  report7: IReport7ApplicantPresentationModel[];
  selectedReportingPeriod?: IReportingPeriodItem;
}

export interface IReport7Presenter extends TReport7Presenter, TPresentable {
  setReportingPeriods(reportingPeriods: IReportingPeriodItem[]): void;
  onReportingPeriodChange(reportingPeriodId?: number): void;
  onApplicantSearch(fullNama: string): void;
  onApplicantsChange(selectedApplicantIds: string[]): void;
  onFormTypeChange(selectedFormTypeCode: string): void;
  onSubmitParams(params: IReport7Params): void;
}

export interface IReport7Params {
  reportingPeriodId: number;
  applicantIds: number[];
  formTypeCode: string;
  selectedFormStructureSectionFullnames: string[];
}

const defaultState: TReport7Presenter = {
  reportingPeriods: [] as IReportingPeriodItem[],
  formTypes: [] as IFormTypeItem[],
  applicants: [] as IReport6Applicant[],
  isFormTypesSelectDisabled: true,
  formStructure: [] as IFormStructureSection[],
  flatFormStructure: [] as IFormStructureSection[],
  report7: [] as IReport7ApplicantPresentationModel[]
};

const Report7Presenter = withStore<IReport7Presenter, TReport7Presenter>(
  ({ application, store, loader, translate }): IReport7Presenter => {
    const _store = store;
    const _application: Application = application;
    const state = _store.getState<TReport7Presenter>();

    const setReportingPeriods = (reportingPeriods: IReportingPeriodItem[]) => {
      _store.update({ reportingPeriods });
    };

    const onReportingPeriodChange = async (reportingPeriodId?: number) => {
      try {
        if (reportingPeriodId) {
          const formTypes = await _application.container
            .resolve<GetFormTypeListInteractor>("getFormTypeList")
            .execute(reportingPeriodId);
          _store.update({ formTypes, isFormTypesSelectDisabled: false });
        } else {
          _store.update({
            formTypes: [] as IFormTypeItem[],
            isFormTypesSelectDisabled: true
          });
        }
      } catch (error) {
        _application.container
          .resolve<ShowErrorMessageInteractor>("showErrorMessage")
          .execute((error as any).message);
      }
    };

    const onApplicantSearch = async (fullName: string) => {
      try {
        if (
          fullName.length >= ReportConstants.AUTOCOMPLETE.APPLICANT_MIN_LENGTH
        ) {
          const applicants = await application.container
            .resolve<GetApplicantsInteractor>("getApplicants")
            .execute(fullName);
          _store.update({
            applicants: applicants || ([] as IReport6Applicant[])
          });
        } else {
          _store.update({ applicants: [] as IReport6Applicant[] });
        }
      } catch (error) {
        application.container
          .resolve<ShowSuccessMessageInteractor>("showSuccessMessage")
          .execute((error as any).message);
      }
    };

    const onApplicantsChange = async (selectedApplicantIds: string[]) => {
      if (selectedApplicantIds.length === 0) {
        _store.update({
          report6Applicants: []
        });
      }
    };

    const onFormTypeChange = async (selectedFormTypeCode: string) => {
      try {
        if (selectedFormTypeCode) {
          let flatFormStructure = await _application.container
            .resolve<GetFormStructureInteractor>("getFormStructure")
            .execute(selectedFormTypeCode);

          flatFormStructure = FormStructureFlatListMapper(flatFormStructure);

          const formStructure = FormStructureMapper(flatFormStructure);

          _store.update({ formStructure, flatFormStructure });
        } else {
          _store.update({
            formStructure: [] as IFormStructureSection[],
            flatFormStructure: [] as IFormStructureSection[]
          });
        }
      } catch (error) {
        _application.container
          .resolve<ShowErrorMessageInteractor>("showErrorMessage")
          .execute((error as any).message);
      }
    };

    const getReport7Data = async (
      reportingPeriodId: number,
      applicantIds: number[],
      formTypeCode: string
    ) => {
      const report7Data = await _application.container
        .resolve<GetReport7DataInteractor>("getReport7Data")
        .execute(reportingPeriodId, applicantIds, formTypeCode);
      return report7Data;
    };

    const getSelectedFormStructureSections = (
      selectedFormStructureSectionFullNames: string[]
    ) => {
      const { flatFormStructure } = _store.getState<IReport7Presenter>();

      return flatFormStructure.filter(
        (formStructureSection: IFormStructureSection) =>
          selectedFormStructureSectionFullNames.includes(
            formStructureSection.fullName
          )
      );
    };

    const setSelectedReportingPeriod = (reportingPeriodId: number) => {
      const { reportingPeriods } = _store.getState<IReport7Presenter>();
      const selectedReportingPeriod = reportingPeriods.find(
        (reportingPeriod: IReportingPeriodItem) =>
          reportingPeriod.id === reportingPeriodId
      );
      _store.update({ selectedReportingPeriod: selectedReportingPeriod });
    };

    const onSubmitParams = async (params: IReport7Params) => {
      try {
        loader.start("report7Loader");
        const report7Data = await getReport7Data(
          params.reportingPeriodId,
          params.applicantIds,
          params.formTypeCode
        );

        setSelectedReportingPeriod(params.reportingPeriodId);

        const selectedFormStructureSections = getSelectedFormStructureSections(
          params.selectedFormStructureSectionFullnames
        );
        let applicants = report7Data.applicants.map(
          (applicant: IReport7Applicant) =>
            Report7ApplicantPresentationModel(applicant)
        );

        const numericalValues = Report7NumericValuePresentationModelList(
          report7Data.numericalValues,
          selectedFormStructureSections,
          applicants
        );

        applicants = Report7ApplicantsMapper(applicants, numericalValues);

        _store.update({ report7: applicants });
        loader.stop("report7Loader");
      } catch (error) {
        loader.stop("report7Loader");
        _application.container
          .resolve<ShowErrorMessageInteractor>("showErrorMessage")
          .execute((error as any).message);
      }
    };

    return {
      ...state,
      store: _store,
      loader,
      application: _application,
      translate,
      setReportingPeriods,
      onReportingPeriodChange,
      onApplicantSearch,
      onApplicantsChange,
      onFormTypeChange,
      onSubmitParams
    };
  },
  defaultState
);

export default Report7Presenter;
